import type { FC } from 'react'
import styled from '@emotion/styled'
import sanitizeHtml from 'sanitize-html'

import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

interface TwilioSegmentRankedProps {
  heading: string
  features: {
    heading: string
    copy: {
      paragraph: string
    }[]
  }[]
  showDisclaimer: boolean
}

const copy = {
  note: `¹Twilio is the #1 CDP for 2022 worldwide market share as determined by IDC.`,
}

const TwilioSegmentRanked: FC<TwilioSegmentRankedProps> = ({ heading, features, showDisclaimer }) => {
  const theme = useTheme()

  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading>{heading}</CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <StyledColumnTrack columns={1} overrides={{ lg: 3 }}>
          {features.map(({ heading, copy }) => (
            <StyledFeatureBoxBase alignment="left" key={heading}>
              <FeatureBox.Body>
                <FeatureBox.Title
                  variant="h4"
                  tag="h3"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(heading, { allowedTags: [`strong`] }),
                  }}
                />
                {copy.map((text, index) => (
                  <Description
                    key={index}
                    tag="p"
                    variant="pMedium"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(text.paragraph, { allowedTags: [`a`] }),
                    }}
                  />
                ))}
              </FeatureBox.Body>
            </StyledFeatureBoxBase>
          ))}
        </StyledColumnTrack>
        {showDisclaimer && (
          <StyledTypography
            variant="pMedium"
            tokens={{ color: theme.palette.twilio.gray[100] }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(copy.note, { allowedTags: [`a`] }),
            }}
          ></StyledTypography>
        )}
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 40px;
    padding-top: 40px;
    padding-bottom: 80px;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      row-gap: 80px;
      padding-top: 80px;
      padding-bottom: 160px;
    }
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  row-gap: 40px;
  align-items: start;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    column-gap: 80px;
    row-gap: 56px;
  }
`

const Description = styled(FeatureBox.Text)``

const StyledTypography = styled(Typography)`
  margin: 0;
  padding-top: 40px;
  text-align: center;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 40px;
  }
`

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  max-width: 520px;
`

export default TwilioSegmentRanked
